import AddIcon from '@atlaskit/icon/core/add';
import { Flex, xcss } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { SeamlessTextfield } from '@databases/form/FormComponents';
import { messages } from '@databases/intl/messages';
import { isModifierKeyPressed } from '@databases/react';
import { DatabaseFieldType, type RawDatabaseValue } from '@databases/types/database';
import {
  type HyperlinkChange,
  MultiValueChangeType
} from '@databases/utils/database/changesets';
import { generateId } from '@databases/utils/utils';
import { noop } from 'lodash-es';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { PopoverEdit } from '../PopoverEdit';
import { usePopoverSoftSelection } from '../popover-edit-common/usePopoverSoftSelection';
import { type EditComponentProps } from '../types';

import { CurrentDataSectionValue } from './HyperlinkValue';

type Value = RawDatabaseValue<DatabaseFieldType.HYPERLINK>[0];

const HyperlinkAddDataContainer = styled.div<{ isRenderedInSidePanel: boolean }>`
  // The text area wrapper must not be a flex box, otherwise the text area is blockified and its height is ignored.
  flex-grow: 1;

  &&& textarea {
    // The height calculation algorithm of @atlaskit/textarea sets the height to auto and then sets the height
    // to its scroll height. With height: auto, a scrollbar appears (except on Atlassian's Mac devices), causing
    // a different scroll height than later when the scroll bar disappears again.
    // Hiding the scrollbar fixes the height calculation algorithm.
    overflow: hidden;
    min-height: 100%;
  }

  > div {
    ${(props) => (props.isRenderedInSidePanel ? 'border: none' : '')}
  }
`;

const BasePaddingStyles = xcss({
  paddingLeft: 'space.150',
  paddingRight: 'space.150'
});

const SidePanelPaddingStyles = xcss({
  paddingTop: 'space.100',
  paddingBottom: 'space.100',
  paddingLeft: 'space.150',
  paddingRight: 'space.150'
});

const HyperlinkValuePopoverEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.HYPERLINK>
> = ({
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  field,
  entryId,
  isInvalid,
  isSeamless,
  contentId,
  isRenderedInSidePanel,
  isBulkEdit,
  ariaLabel
}) => {
  const addDataSectionRef = useRef<HTMLTextAreaElement | null>(null);
  const [hasCurrentData, setHasCurrentData] = useState<boolean>(false);
  const { formatMessage } = useIntl();
  usePopoverSoftSelection({
    entryId,
    fieldId: field.id,
    isRenderedInSidePanel
  });

  // On initial render and if multi setting changes, determine if current data exists
  useEffect(() => {
    setHasCurrentData(value.length > 0 && value.some((link) => link.url !== ''));
  }, [field.settings.multi]); // eslint-disable-line react-hooks/exhaustive-deps

  const Component = isSeamless ? SeamlessTextfield : Textfield;
  // Check for length greater than one ensures that existing multi value cells
  // will remain multi value regardless of the field setting
  const isMulti = field.settings.multi || value.length > 1;
  const addLinkMessage = formatMessage(
    messages['hyperlink-value-popover-edit.add-hyperlink-value']
  );

  const addHyperlinkValueToCollab = React.useCallback(
    (val: Value, allValues: Value[]) => {
      if (val.url === '') {
        return;
      }

      const hyperlinkValues = [...allValues, val];

      const delta: HyperlinkChange = isMulti
        ? {
            type: MultiValueChangeType.APPEND,
            value: val
          }
        : {
            type: MultiValueChangeType.SET_TO,
            value: [val]
          };

      onChange(hyperlinkValues, {
        type: DatabaseFieldType.HYPERLINK,
        delta
      });
    },
    [isMulti, onChange]
  );

  const setHyperlinkValue = useCallback(
    (
      e:
        | React.KeyboardEvent<HTMLTextAreaElement>
        | React.FocusEvent<HTMLTextAreaElement, Element>
    ) => {
      if (e.currentTarget.value) {
        const newLink = { _id: generateId(), url: e.currentTarget.value };
        addHyperlinkValueToCollab(newLink, value);
        setHasCurrentData(true);
        e.currentTarget.value = ''; // Ensure empty text for addDataSection
      }
    },
    [addHyperlinkValueToCollab, value]
  );

  const handleAddDataSectionKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!isModifierKeyPressed(e) && e.key === 'Enter') {
        e.preventDefault();
        setHyperlinkValue(e);
      }
    },
    [setHyperlinkValue]
  );

  const handleAddDataSectionBlur = useCallback(
    (event: React.FocusEvent<HTMLTextAreaElement, Element>) => {
      // If some text exists, append it to the value array
      setHyperlinkValue(event);
      onBlur?.(event);
      if (!isRenderedInSidePanel) {
        addDataSectionRef.current?.focus();
      }
    },
    [setHyperlinkValue, onBlur, isRenderedInSidePanel]
  );

  const addDataSection = useMemo(
    () => (
      <Flex
        justifyContent={'center'}
        alignItems={'center'}
        xcss={[BasePaddingStyles, isRenderedInSidePanel && SidePanelPaddingStyles]}
      >
        <AddIcon label="" />
        <HyperlinkAddDataContainer isRenderedInSidePanel={isRenderedInSidePanel}>
          <Component
            placeholder={addLinkMessage}
            onChange={noop}
            onFocus={onFocus}
            onBlur={handleAddDataSectionBlur}
            onKeyDown={handleAddDataSectionKeyDown}
            autoFocus={autoFocus}
            isInvalid={isInvalid}
            ref={addDataSectionRef}
            aria-label={ariaLabel}
            // Override @atlaskit/textfield's font.body.large to match the cell's 14px font size.
            style={{ font: token('font.body', '14px') }}
          />
        </HyperlinkAddDataContainer>
      </Flex>
    ),
    [
      addLinkMessage,
      onFocus,
      handleAddDataSectionBlur,
      handleAddDataSectionKeyDown,
      autoFocus,
      isInvalid,
      isRenderedInSidePanel,
      Component,
      ariaLabel
    ]
  );

  const currentDataSection = useMemo(
    () => (
      <>
        {value.map((link) => (
          <CurrentDataSectionValue
            key={link._id}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            autoFocus={autoFocus}
            value={value}
            link={link}
            setHasCurrentData={setHasCurrentData}
            contentId={contentId}
            field={field}
            isMulti={isMulti}
          />
        ))}
      </>
    ),
    [value, onChange, onFocus, onBlur, autoFocus, field, contentId, isMulti]
  );
  return (
    <PopoverEdit
      isMultiSelect={isMulti}
      hasCurrentData={isMulti ? value.length > 0 : hasCurrentData}
      currentDataSection={currentDataSection}
      addDataSection={addDataSection}
      isRenderedInSidePanel={isRenderedInSidePanel}
      fieldWidth={field.width}
      isBulkEdit={isBulkEdit}
    />
  );
};

export default HyperlinkValuePopoverEdit;
